import type {Theme} from "@glideapps/glide-data-grid";
import {getOtsThemeVariables, type OtsBrand, type OtsColorScheme} from "../theme/otsThemeTokens";

/** Canvas colors use the same tokens as DOM controls, including a readable selection accent. */
export function getDataGridTheme(scheme: OtsColorScheme, brand: OtsBrand = "corporate"): Partial<Theme> {
  if (scheme === "light") return {};
  const colors = getOtsThemeVariables(brand, scheme);
  return {
    textDark: colors["--ots-text"],
    textMedium: colors["--ots-text-muted"],
    textLight: "#91a5bc",
    textBubble: colors["--ots-text"],
    textHeader: colors["--ots-text"],
    textHeaderSelected: colors["--ots-on-primary"],
    textGroupHeader: colors["--ots-text"],
    bgIconHeader: colors["--ots-text-muted"],
    fgIconHeader: colors["--ots-field"],
    bgCell: colors["--ots-field"],
    bgCellMedium: colors["--ots-surface"],
    bgHeader: colors["--ots-surface"],
    bgHeaderHasFocus: colors["--ots-surface-variant"],
    bgHeaderHovered: colors["--ots-surface-variant"],
    bgBubble: colors["--ots-neutral-container"],
    bgBubbleSelected: colors["--ots-primary-container"],
    bgSearchResult: colors["--ots-warning-container"],
    borderColor: colors["--ots-outline-variant"],
    horizontalBorderColor: colors["--ots-outline-variant"],
    drilldownBorder: colors["--ots-outline"],
    linkColor: colors["--ots-blue"],
    accentColor: colors["--ots-primary"],
    accentFg: colors["--ots-on-primary"],
    accentLight: `${colors["--ots-primary"]}3d`,
  };
}

/** Keep the established light separator while giving dark canvases their own surface and text. */
export function getDataGridSeparatorTheme(scheme: OtsColorScheme = "light"): Partial<Theme> {
  const colors = getOtsThemeVariables("corporate", scheme);
  return {
    bgCell: scheme === "dark" ? colors["--ots-surface-variant"] : "#e5e7eb",
    textDark: scheme === "dark" ? colors["--ots-text"] : "#374151",
    textMedium: scheme === "dark" ? colors["--ots-text-muted"] : "#4b5563",
    baseFontStyle: "600 13px",
    horizontalBorderColor: scheme === "dark" ? colors["--ots-outline"] : "#d1d5db",
  };
}

/** Success/attention row tints for dense grids; neutral rows inherit the canvas outside hover. */
export function getDataGridStatusRowTheme(
  tone: "success" | "warning" | "neutral" | "danger" | "unassigned",
  scheme: OtsColorScheme,
  hovered = false
): Partial<Theme> | undefined {
  if (scheme === "light") {
    const bgCell =
      tone === "danger"
        ? hovered
          ? "#FFE5E5"
          : "#FFBFBF"
        : tone === "unassigned"
        ? hovered
          ? "#faedda"
          : "#f3d4a3"
        : tone === "success"
        ? hovered
          ? "#acb696"
          : "#d8e4bc"
        : tone === "warning"
        ? hovered
          ? "#afaf66"
          : "#fafa93"
        : hovered
        ? "#f0f0f0"
        : undefined;
    return bgCell ? {bgCell} : undefined;
  }
  if (tone === "neutral" && !hovered) return undefined;
  const colors = getOtsThemeVariables("corporate", scheme);
  const bgCell =
    tone === "danger"
      ? colors["--ots-danger-container"]
      : tone === "unassigned"
      ? colors["--ots-warning-container"]
      : tone === "success"
      ? hovered
        ? "#204d34"
        : colors["--ots-success-container"]
      : tone === "warning"
      ? hovered
        ? "#513d14"
        : colors["--ots-warning-container"]
      : colors["--ots-surface-variant"];
  return {
    bgCell,
    bgCellMedium: bgCell,
    textDark:
      tone === "danger"
        ? colors["--ots-on-danger-container"]
        : tone === "unassigned"
        ? colors["--ots-on-warning-container"]
        : tone === "success"
        ? colors["--ots-on-success-container"]
        : tone === "warning"
        ? colors["--ots-on-warning-container"]
        : colors["--ots-text"],
    textMedium: colors["--ots-text-muted"],
  };
}

export function getDataGridShiftBadgeColors(status: string, scheme: OtsColorScheme = "light") {
  const active = status === "In Shift";
  const off = status === "Off Shift";
  if (scheme === "light")
    return {
      bg: active ? "#40c057" : off ? "#f1f3f5" : "#228be6",
      text: off ? "#495057" : "#ffffff",
      border: off ? "#dee2e6" : undefined,
    };
  const colors = getOtsThemeVariables("corporate", scheme);
  return {
    bg: active
      ? colors["--ots-success-container"]
      : off
      ? colors["--ots-neutral-container"]
      : colors["--ots-blue-container"],
    text: active
      ? colors["--ots-on-success-container"]
      : off
      ? colors["--ots-on-neutral-container"]
      : colors["--ots-on-blue-container"],
    border: active ? colors["--ots-success-outline"] : off ? colors["--ots-outline"] : colors["--ots-blue-outline"],
  };
}
